import React from "react";
import { FormProvider } from "react-hook-form";
import { Button, Dialog, Form } from "@sscs/ui";
import { MultiEmailField } from "Components";
import GroupRoleAutocomplete from "../GroupRoleAutocomplete/GroupRoleAutocomplete";
import OrganizationRoleAutocomplete from "../OrganizationalRoleAutocomplete/OrganizationRoleAutocomplete";
import useInviteMember from "./useInviteMember";

type InviteMemberProps = {
  isOpen: boolean;
  onClose: () => void;
};

const InviteMemberContent = ({
  onClose,
}: Omit<InviteMemberProps, "isOpen">): React.JSX.Element | null => {
  const { form, groups, onSubmit } = useInviteMember(onClose);

  if (!groups) return null;

  const groupIds: string[] = groups
    .filter((group) => group.group_id !== undefined)
    .map((group) => group.group_id as string);

  const {
    formState: { errors: errors },
    watch,
  } = form;

  const groupRoleRequired = !!watch("orgGroups", []).length;

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>Invite member</Dialog.Header.Title>
      </Dialog.Header>
      <FormProvider {...form}>
        <Form.Form onSubmit={onSubmit}>
          <Dialog.Body>
            <Dialog.Section>
              <Dialog.Section.Content>
                <MultiEmailField
                  id="invite-member-email"
                  fieldName="emails"
                  required
                />
                <OrganizationRoleAutocomplete id="invite-member-org-role" />
                <Form.Autocomplete
                  name="orgGroups"
                  label="Groups"
                  errors={errors}
                  options={groupIds}
                  multiSelect={true}
                  getOptionLabel={(option) =>
                    groups.find((group) => group.group_id === option)?.name ??
                    ""
                  }
                />
                <GroupRoleAutocomplete
                  id="invite-member-group-role"
                  isRequired={groupRoleRequired}
                />
              </Dialog.Section.Content>
            </Dialog.Section>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Footer.Actions>
              <Button color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" color="primary">
                Invite member
              </Button>
            </Dialog.Footer.Actions>
          </Dialog.Footer>
        </Form.Form>
      </FormProvider>
    </>
  );
};

const InviteMember = ({
  isOpen,
  ...props
}: InviteMemberProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <InviteMemberContent {...props} />
  </Dialog.Frame>
);

export default InviteMember;
